import React from "react";
import { Link } from "@remix-run/react";
import { UseDataFunctionReturn } from "remix-typedjson";

import { useEffect } from "react";
import {
  type ActionFunctionArgs,
  type LoaderFunctionArgs,
  useContext,
  useElements,
  useState,
  useStripe } from
"../deps/remix.ts";
import { Stripe, StripeClient } from "../lib/stripe-client.ts";

import { isTest } from "~/lib/doess.ts";

import { merge } from "~/deps/merge.ts";
import { z } from "~/deps/zod.ts";
import { PaymentMethod } from "~/models/striped/payment-method.server.ts";
import {
  Fieldset,
  FieldsManager,
  FormButton,
  FormContext,
  FormField,
  FormInputType,
  PageContribute,
  PaymentSelectionOptionsField,
  ValidatedForm,
  WrapStripeElements } from
"../components.ts";
import ContributionOptionsField from "../components/contribution-options-field.tsx";
import { useBindings } from "../hooks/use-bindings.ts";
import { useResult } from "../hooks/use-result.ts";
import { RequestContext } from "../lib/request-context.ts";
import {
  confirm,
  fail,
  getSession,
  render,
  requestParams,
  resolveCurrentCustomer,
  resolveCurrentUser,
  supabaseClient } from
"../route-helpers.server.ts";
import { trackGAEvent, trackParselyConversion } from "../route-helpers/analytics-event-helpers.ts";

import { SigninController } from "~/controllers/signin-controller.ts";
import { useFields } from "~/hooks/use-fields.ts";
import { nicePrice } from "~/lib/money.ts";
import ContributionTickerCSS from "../styles/contribution-ticker.css";
import PageContributeCSS from "../styles/page-contribute.css";
export function links() {
  return [
  { rel: "stylesheet", href: ContributionTickerCSS },
  { rel: "stylesheet", href: PageContributeCSS }];

}

const calculateFee = (baseAmount: number) => {
  let feeAmount = baseAmount * 0.029;
  feeAmount = Math.round(feeAmount + 30);
  return feeAmount;
};

const calculateTotal = (baseAmount: number, coverFee: boolean) => {
  return coverFee ? baseAmount + calculateFee(baseAmount) : baseAmount;
};

export const formFields: Fieldset = {
  email: {
    label: "",
    placeholder: "Email Address",
    type: FormInputType.Email
  },

  contribution_amount: {
    label: "Contribution Amount",
    type: (FormInputType.Radio as const)
  },

  cover_fee: {
    label: "",
    type: (FormInputType.Checkbox as const)
  },

  /*
    payment_method is a set of radio inputs. It's values are the stored payment method ids for current customer
    pm_id is the new payment method id created by stripe-js
     if payment_method is set, we use that value. otherwise we use pm_id
     The value needs to be present in the form html so we can submit. This is how I'm doing that.
    See the beforeSubmit function in ContributeInner for specifics on pm_id's usage
   */


  payment_method: {
    label: "Payment Method",
    type: FormInputType.Radio
  },
  pm_id: {
    label: "",
    type: FormInputType.Hidden
  },
  intent: {
    label: "",
    type: FormInputType.Hidden
  }
};

export const paramsSchema = z.object({
  email: z.string().optional(),
  contribution_amount: z.string().optional(),
  payment_method: z.string().optional(),
  pm_id: z.string().optional(),
  cover_fee: z.string().optional(),
  intent: z.string().optional()
});

export const loader = async (env: LoaderFunctionArgs) => {
  const ctx = new RequestContext(env.request, formFields);

  const params = paramsSchema.safeParse(await requestParams(ctx));
  if (!params.success) {
    throw new Error(params.error.message);
  }

  const currentUser = await resolveCurrentUser(ctx);

  // ⚠️ since these formfields want to have default values derived from either
  // a request param or the current user, we need to merge them here instead
  // of setting them in the formFields object above
  ctx.formFields = merge(formFields, {
    contribution_amount: {
      value: params.data.contribution_amount ? params.data.contribution_amount : "10000"
    },
    intent: { value: currentUser ? "contribute" : "signin" }
  });

  return render(ctx, {
    ident: true,
    currentCustomer: true,
    contributionTotal: true
  });
};

export const action = async (env: ActionFunctionArgs) => {
  const ctx = new RequestContext(env.request);

  try {
    const reqParams = await requestParams(ctx);
    const params = paramsSchema.safeParse(reqParams);

    if (!params.success) {
      return fail(ctx, { message: params.error.message });
    }

    /* if intent === signin, do that */
    if (reqParams.intent === "signin") {
      const supabase = supabaseClient(ctx);

      const { email } = params.data;

      if (!email || email === undefined) {
        return fail(ctx, { message: "Email is required" });
      }

      let queryParams = [];

      if (reqParams.contribution_amount) {
        queryParams.push(`contribution_amount=${reqParams.contribution_amount}`);
      }
      let nextPath = `/contribute`;
      if (queryParams.length) {
        nextPath += encodeURIComponent(`?${queryParams.join("&")}`);
      }

      const signInController = new SigninController(params.data, supabase, ctx);
      const resp = await signInController.emailSignIn(nextPath);

      if (resp.error) {
        return fail(ctx, { message: resp.error.message });
      }

      return confirm(ctx, {
        message: `A sign in link has been sent to ${params.data.email}.`
      });
    }
    /* else, contribute: */

    /* normalize the payment method string */
    let paymentMethodId = params.data.payment_method || params.data.pm_id;
    if (!paymentMethodId) {
      return fail(ctx, { message: "Contribute: no payment method found" });
    }

    /* attach payment method to customer and upsert */
    let cus = await resolveCurrentCustomer(ctx);
    if (!cus) {
      //check hcaptcha
      const hcaptchaResponse = reqParams["h-captcha-response"];
      const hcaptchaSecret = Deno.env.get("HCAPTCHA_SECRET_KEY");
      const response = await fetch("https://api.hcaptcha.com/siteverify", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: `response=${hcaptchaResponse}&secret=${hcaptchaSecret}`
      });

      if (!response.ok) {
        return fail(ctx, { message: "Failed to verify hCaptcha response" });
      }

      const data = await response.json();

      if (!data.success) {
        return fail(ctx, { message: "hCaptcha verification failed" });
      }

      //create a new customer
      if (!params.data.email) {
        return fail(ctx, { message: "Email is required" });
      }
      cus = await StripeClient.createCustomer({ email: params.data.email });
      if (!cus) {
        return fail(ctx, {
          message: "An error occuring while attempting to create a Stripe customer."
        });
      }
      await StripeClient.attachPaymentMethodToCustomer(paymentMethodId, cus.id);
      await PaymentMethod.upsert(paymentMethodId);
    } else {
      await StripeClient.attachPaymentMethodToCustomer(paymentMethodId, cus.id);
      await PaymentMethod.upsert(paymentMethodId);
    }

    /*
      in test mode PMs are created from a paymentmethod string, i.e. pm_card_visa
      here, we need to look up the customer's payment methods and use the first one
      there's only going to be 1 in test mode, that's how the test is written
       in live mode this is unncessary b/c stripe-js returns a paymentMethodId in the browser
    */

    if (isTest()) {
      const PMs = await StripeClient.stripe.customers.listPaymentMethods(cus.id);
      if (PMs.data.length === 0) {
        throw new Error(`Contribute: no payment methods found for ${cus.id}`);
      }
      paymentMethodId = PMs.data[0].id;
    }

    /*
      idemptKey
    */
    const session = await getSession(ctx);
    session.get("contributionIdempentKey") ??
    session.set("contributionIdempentKey", RequestContext.randomUUID());
    const idempotencyKeyBase = `${session.get("contributionIdempentKey")}`;

    /*
      create draft Invoice using the paymentMethodId
    */
    const invoice = await StripeClient.createInvoice({
      customer: cus.id,
      default_payment_method: paymentMethodId,
      metadata: {
        "doess.contribution": "true"
      }
    }, {
      idempotencyKey:
      `create-invoice-${idempotencyKeyBase}-${params.data.contribution_amount}-${paymentMethodId}`
    });

    /* attach an InvoiceItem to the Invoice. This creates a single-use price a well */
    let unit_amount = parseInt(params.data.contribution_amount);
    if (params.data.cover_fee) {
      unit_amount += calculateFee(unit_amount);
    }

    const invoiceItem = await StripeClient.createInvoiceItem({
      customer: cus.id,
      invoice: invoice.id,
      price_data: {
        currency: "usd",
        product: Deno.env.get("DOESS_CONTRIBUTION_PRODUCT_ID"),
        unit_amount: unit_amount
      }
    }, {
      idempotencyKey: `create-invoice-item-${idempotencyKeyBase}-${invoice.id}`
    });

    /* Pay the Invoice */
    const paidInvoice = await StripeClient.payInvoice(invoice.id, {
      idempotencyKey: `pay-invoice-${idempotencyKeyBase}-${invoice.id}`
    });

    trackGAEvent(ctx, "contribution", paidInvoice);

    return confirm(ctx, {
      message: "Contribution confirmed",
      paid: paidInvoice.paid,
      amount_paid: paidInvoice.amount_paid
    });
  } catch (err) {
    if (err instanceof Stripe.errors.StripeCardError) {
      return fail(ctx, { message: err.message });
    } else {
      return fail(ctx, { message: "Something went wrong, " + err.message });
    }
  }
};

export default function ContributeWrapper() {
  const { ident, currentCustomer } = useBindings<typeof loader>();
  const { confirmation, errors } = useResult<typeof action>();
  const manager = useFields<UseDataFunctionReturn<typeof loader>["formFields"]>();

  const [withAccount, setWithAccount] = useState(!currentCustomer);

  if (!currentCustomer && withAccount) {
    return (
      <FormContext.Provider value={manager}>
        <PageContribute>
          <ValidatedForm manager={manager}>
            <div style={{ marginTop: "8px" }}>
              <label
                style={{
                  fontWeight: "bold",
                  fontSize: "15px",
                  display: "block",
                  textAlign: "center"
                }}>

                Enter email to sign in or create new account
              </label>
              <FormField name="email" />
              {confirmation &&

              <div style={{ margin: "8px 16px 24px" }}>
                    <h2 style={{ margin: "0" }}>Email sent!</h2>
                    <p style={{ margin: "0.5em 0" }}>{confirmation.message!}</p>
                    <p style={{ margin: "0.5em 0" }}>
                      Don’t see an email? Please check your spam folder or{" "}
                      <Link
                    to="https://talkingpointsmemo.com/contact"
                    style={{ color: "#990000" }}>

                        contact&nbsp;us
                      </Link>{" "}
                      for assistance.
                    </p>
                  </div>}

              <FormButton className="--red">
                SEND EMAIL LINK
              </FormButton>
              <div style={{ textAlign: "center", marginBottom: "-8px" }}>OR</div>
              <div
                className="Button --grey-empty"
                style={{ marginTop: "16px", color: "#111", borderColor: "#111" }}
                onClick={() => {
                  setWithAccount(!withAccount);
                }}>

                CONTINUE WITHOUT AN ACCOUNT
              </div>
            </div>
          </ValidatedForm>
        </PageContribute>
      </FormContext.Provider>);

  }

  return (
    <FormContext.Provider value={manager}>
      <PageContribute>
        {!ident.email && !confirmation ?

        <div
          className="PageContribute__ToggleSignIn"
          onClick={() => {
            setWithAccount(!withAccount);
          }}>

              Back
            </div> :

        null}
        <WrapStripeElements
          opts={{ mode: "setup", currency: "usd", paymentMethodCreation: "manual" }}>

          <Contribute />
        </WrapStripeElements>
      </PageContribute>
    </FormContext.Provider>);

}

function Contribute() {
  const { ident, currentCustomer } = useBindings();
  const stripe = useStripe();
  const elements = useElements();

  const { confirmation, errors } = useResult<typeof action>();

  const manager = useContext(FormContext);

  const message = confirmation && confirmation.amount_paid ?
  `I contributed ${
  nicePrice(parseInt(confirmation.amount_paid))
  } to the TPM Journalism Fund. Will you match my contribution?` :
  "I contributed to the TPM Journalism Fund!";

  if (confirmation && confirmation.amount_paid) {
    trackParselyConversion({
      type: "purchase",
      value: `contribution ${nicePrice(confirmation.amount_paid)}`,
      idempotencyKey: "tpmTrackedParselyPurchase"
    });

    return (
      <div className="PageContribute__Summary">
        <h1>THANK&nbsp;YOU FOR YOUR&nbsp;CONTRIBUTION!</h1>
        <p>
          Know someone who might want to match your contribution? You can help us spread the word by
          encouraging others to contribute.
        </p>
        <p>
          <b>
            "{message}"
          </b>
        </p>
        <div className="PageContribute__ShareLinks">
          <a
            className="PageContribute__ShareLink"
            onClick={() => {
              window.open(
                `http://www.facebook.com/sharer.php?u=${document.location.origin}${document.location.pathname}&amp;t=I%20just%20contributed%20to%20the%20TPM%20Journalism%20Fund.%20Will%20you%20match%20my%20contribution%3F`,
                "newwindow",
                "width=530, height=450"
              );
              return false;
            }}
            target="blank"
            aria-label="share to facebook">

            <i className="fab fa-facebook-f" aria-hidden="true"></i>
          </a>
          <a
            className="PageContribute__ShareLink"
            onClick={() => {
              window.open(
                `https://twitter.com/intent/tweet?text=I just contributed to the TPM Journalism Fund. Will you match my contribution%3F&url=${document.location.origin}${document.location.pathname}`,
                "newwindow",
                "width=530, height=450"
              );
              return false;
            }}
            target="blank"
            aria-label="share to twitter">

            <i className="fab fa-twitter" aria-hidden="true"></i>
          </a>

          <a
            className="PageContribute__ShareLink"
            onClick={() => {
              window.open(
                `mailto:?subject=I%20just%20contributed%20to%20the%20TPM%20Journalism%20Fund.%20Will%20you%20match%20my%20contribution%3F&amp;body=${
                encodeURIComponent(
                  "https://accounts.talkingpointsmemo.com/contribute"
                )
                }`,
                "newwindow",
                "width=530, height=450"
              );
              return false;
            }}
            target="blank"
            aria-label="share via email">

            <i className="far fa-envelope" aria-hidden="true"></i>
          </a>
        </div>
      </div>);

  }

  const beforeSubmit = async (manager: FieldsManager) => {
    if (!stripe || !elements) {
      return;
    }

    if (manager.getValue("payment_method")) return;

    const submitRes = await elements.submit();
    if (submitRes.error) {
      return submitRes.error.message ?
      [submitRes.error.message] :
      ["A Stripe error occured when submitting the form."];
    }

    const pmRes = await stripe.createPaymentMethod({
      elements,
      params: {
        billing_details: {}
      }
    });

    if (pmRes.error) {
      return pmRes.error.message ?
      [pmRes.error.message] :
      ["A Stripe error occured when adding the payment method."];
    }

    /*
    setValue calls the reducer's dispatch func, which WILL NOT update the state until the next render
    https://react.dev/reference/react/useReducer#dispatch
     Instead of using setValue, we set the manager state **outside** of the reducer.
    This is generally bad practice but:
    1.) were going to submit the form immediately after this before hook executes
    2.) it works
    */

    manager.state.pm_id.value = pmRes.paymentMethod.id;
  };

  return (
    <ValidatedForm beforeSubmit={beforeSubmit} manager={manager!}>
      <ContributeInner />
    </ValidatedForm>);

}

/* this is it's own component b/c we need to access the fields manager to print the fee amount */
function ContributeInner() {
  const ident = useBindings<typeof loader>().ident;
  const manager = useContext(FormContext);

  let totalAmount = calculateTotal(
    parseInt(manager.get("contribution_amount").value),
    manager.get("cover_fee").value === "true"
  );

  if (Number.isNaN(totalAmount)) totalAmount = "";else
  totalAmount = nicePrice(totalAmount);

  useEffect(() => {
    if (ident.email) return;

    const script = document.createElement("script");
    script.src = "https://js.hcaptcha.com/1/api.js";
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }, []);
  return (
    <div>
      <section className="Section --no-border --no-padding-top">
        <ContributionOptionsField />
      </section>
      {ident.email ?

      <section className="Section --grey">
            <div className="Section__Title">
              <span>ACCOUNT</span>
            </div>
            <div className="Section__Inner">
              <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "8px"
            }}>

                {ident.email}
                <Link to="/account/email" style={{ color: "#111", fontSize: "13px" }}>Change</Link>
              </div>
            </div>
          </section> :


      <div>
            <label style={{ fontWeight: "bold", fontSize: "15px" }}>EMAIL</label>
            <FormField name="email" />
          </div>}

      <section className="Section --no-border">
        <div className="Section__Title">
          <span style={{ fontWeight: "bold", fontSize: "15px" }}>PAYMENT</span>
        </div>
        <div className="Section__Inner">
          <PaymentSelectionOptionsField />
          {!ident.email &&
          <div style={{ marginTop: "16px" }}>
              <div
              className="h-captcha"
              // ⚠️ whats a better way to set this value per-environment? can't use Deno.env in the browser
              data-sitekey={window.location.hostname === "accounts.talkingpointsmemo.com" ?
              "2ef810f3-2538-45d9-9530-26dd8323e792" :
              "9f40ace8-3a18-456b-ad21-252af9eba17a"}>

              </div>
            </div>}

          <label style={{ display: "flex", fontSize: "15px", lineHeight: 1, alignItems: "center" }}>
            <FormField name="cover_fee" />
            <span>
              I’d like to cover the {manager?.get("contribution_amount").value &&
              nicePrice(calculateFee(parseInt(manager.get("contribution_amount").value)))}{" "}
              processing fee so that TPM receives my full donation.
            </span>
          </label>
        </div>
      </section>
      <FormField name="pm_id" />
      <FormButton className="--red">
        {`Contribute ${totalAmount}`}
      </FormButton>

      <p
        style={{
          fontSize: "15px",
          borderTop: "1px solid rgba(153, 153, 153, 1)",
          paddingTop: "16px",
          margin: "16px 0 0 0"
        }}>

        This is a one-time contribution and is not tax-deductible as a charitable contribution.
      </p>
    </div>);

}